import React, { useMemo } from 'react';
import Button from '../../../../../ui/canon/Button';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  DownloadIcon,
  Share2Icon,
  ArrowRightIcon,
  TrendingUpIcon,
  SparklesIcon,
} from 'lucide-react';
import { calculateOwnerDiagnosticScores } from '../../../lib/ownerDiagnosticScoring';

/**
 * SuccessionReadinessAssessment - Visual assessment report showing readiness scores and gaps
 * 
 * @param {Object} props
 * @param {Object} props.progress - The user's plan progress data
 * @param {string} props.userName - The user's name
 * @param {Object} props.storedScores - Stored VMI scores from backend
 * @param {Function} props.onDownload - Download handler
 * @param {Function} props.onShare - Share handler
 */
const SuccessionReadinessAssessment = ({
  progress,
  userName,
  storedScores,
  onDownload,
  onShare,
  className = '',
}) => {
  // Calculate readiness assessment from responses
  const assessment = useMemo(() => {
    return calculateReadinessAssessment(progress, storedScores);
  }, [progress, storedScores]);

  const generatedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical':
        return 'text-red-600 dark:text-red-400';
      case 'needs-work':
        return 'text-orange-600 dark:text-orange-400';
      case 'developing':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'good':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'critical':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'needs-work':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'developing':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'good':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical':
        return <AlertCircleIcon className="h-5 w-5" />;
      case 'needs-work':
        return <AlertTriangleIcon className="h-5 w-5" />;
      case 'developing':
        return <AlertTriangleIcon className="h-5 w-5" />;
      case 'good':
        return <CheckCircleIcon className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'critical':
        return 'Critical';
      case 'needs-work':
        return 'Needs Work';
      case 'developing':
        return 'Developing';
      case 'good':
        return 'Good';
      default:
        return 'Unknown';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityStripeColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'medium':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getOverallMessage = (score) => {
    if (score < 50) {
      return 'Significant preparation needed before succession';
    } else if (score < 70) {
      return 'You have further work to do.';
    } else if (score < 85) {
      return 'Good progress, some areas need attention';
    } else {
      return 'Well-prepared for succession';
    }
  };

  return (
    <div className={`min-h-full bg-succession-bg-secondary ${className}`}>
      <div className="max-w-6xl mx-auto p-8 space-y-10">
        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-4xl font-bold tracking-tight text-succession-text-primary">
                Succession Readiness Assessment
              </h1>
              <p className="text-succession-text-muted">
                Generated on {generatedDate}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {onShare && (
                <Button
                  onClick={onShare}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Share2Icon className="h-4 w-4" />
                  Share
                </Button>
              )}
              {onDownload && (
                <Button
                  onClick={onDownload}
                  size="sm"
                  className="gap-2 bg-succession-primary hover:bg-succession-primary/90 text-white"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Download
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Overall Readiness Score - Hero Section */}
        <div className="bg-succession-bg-primary border border-succession-border rounded-xl overflow-hidden">
          <div className="bg-gradient-to-br from-succession-bg-secondary via-succession-bg-primary to-transparent p-10">
            <div className="text-center space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-succession-bg-primary border border-succession-border">
                <span className="text-sm font-medium text-succession-text-muted">
                  Overall Readiness Score
                </span>
              </div>

              <div className="flex items-center justify-center gap-4">
                <div className={`text-8xl font-bold tracking-tight ${getStatusColor(assessment.overallStatus)}`}>
                  {assessment.overallScore}%
                </div>
                <div className={getStatusColor(assessment.overallStatus)}>
                  {getStatusIcon(assessment.overallStatus)}
                </div>
              </div>

              <div className="max-w-2xl mx-auto space-y-4">
                <p className="text-lg text-succession-text-secondary">
                  {getOverallMessage(assessment.overallScore)}
                </p>

                <div className="space-y-2">
                  {/* Progress Bar */}
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-succession-secondary rounded-full transition-all duration-500"
                      style={{ width: `${assessment.overallScore}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs text-succession-text-muted">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Value Acceleration CTA Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-succession-secondary border border-succession-border shadow-succession-lg group">
          {/* Background decoration */}
          <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-15 transition-opacity">
            <TrendingUpIcon className="w-32 h-32 text-white" strokeWidth={1.5} />
          </div>
          
          <div className="relative z-10 px-4 py-3 md:px-6 md:py-5 lg:px-8 lg:py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <SparklesIcon className="w-6 h-6 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg md:text-xl font-bold text-white">
                    Ready to Take Action?
                  </h3>
                  <p className="text-sm md:text-base max-w-lg leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                    Now that you have your readiness assessment, head to the Value Building page to see your personalized action plan, close your gaps, and accelerate your business value.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Button
                  as="a"
                  href="/succession/value_acceleration?from=readiness_assessment"
                  className="rounded-full"
                >
                  Go to Value Building
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Calculate readiness assessment from plan progress and stored scores
 */
function calculateReadinessAssessment(progress, storedScores) {
  const responses = progress?.userPlanResponses || [];
  const modules = progress?.planTemplate?.planModules || [];

  // Build a map of question ID to question data
  const questionMap = {};
  modules.forEach((mod) => {
    mod.planSections?.forEach((sec) => {
      sec.planQuestions?.forEach((q) => {
        questionMap[q.id] = {
          ...q,
          moduleTitle: mod.title,
          sectionTitle: sec.title,
        };
      });
    });
  });

  // Build response map
  const responseMap = {};
  responses.forEach((r) => {
    responseMap[r.planQuestionId] = r.answer?.value || r.answer;
  });

  // Define readiness categories based on question purposes/categories
  const ownerQuestionIds = [];
  const businessQuestionIds = [];

  // Categorize questions based on their category or output_section
  Object.entries(questionMap).forEach(([id, q]) => {
    const category = (q.category || '').toLowerCase();
    const outputSection = (q.outputSection || '').toLowerCase();
    
    if (
      category.includes('owner') ||
      category.includes('personal') ||
      category.includes('financial') ||
      category.includes('tax') ||
      outputSection.includes('owner') ||
      outputSection.includes('goals') ||
      outputSection.includes('timeline')
    ) {
      ownerQuestionIds.push(id);
    } else if (
      category.includes('business') ||
      category.includes('valuation') ||
      category.includes('operations') ||
      outputSection.includes('business') ||
      outputSection.includes('valuation') ||
      outputSection.includes('practice')
    ) {
      businessQuestionIds.push(id);
    }
  });

  // If no categorization found, split evenly
  if (ownerQuestionIds.length === 0 && businessQuestionIds.length === 0) {
    const allIds = Object.keys(questionMap);
    const midpoint = Math.ceil(allIds.length / 2);
    ownerQuestionIds.push(...allIds.slice(0, midpoint));
    businessQuestionIds.push(...allIds.slice(midpoint));
  }

  // Calculate category scores
  const calculateCategoryScore = (questionIds) => {
    if (questionIds.length === 0) return { score: 0, answered: 0, total: 0 };
    
    const answered = questionIds.filter((id) => responseMap[id] !== undefined);
    let qualityScore = 1.0;

    answered.forEach((id) => {
      const answer = String(responseMap[id] || '').toLowerCase();
      // Penalize "no" answers
      if (answer === 'no' || answer === 'false') {
        qualityScore -= 0.1;
      }
      // Reward detailed answers
      if (answer.length > 50) {
        qualityScore += 0.05;
      }
    });

    qualityScore = Math.max(0.5, Math.min(1.2, qualityScore));
    const completionRate = answered.length / questionIds.length;
    const score = Math.round(completionRate * 100 * qualityScore);

    return {
      score: Math.min(100, score),
      answered: answered.length,
      total: questionIds.length,
    };
  };

  const getStatus = (score) => {
    if (score < 50) return 'critical';
    if (score < 70) return 'needs-work';
    if (score < 85) return 'developing';
    return 'good';
  };

  // Calculate completion stats from plan responses (for progress display)
  const ownerCalc = calculateCategoryScore(ownerQuestionIds);
  const businessCalc = calculateCategoryScore(businessQuestionIds);

  // Use the 12-question diagnostic scores (same as Value Acceleration page)
  // This uses diag_owner_answers and diag_business_answers arrays
  const diagnosticScores = calculateOwnerDiagnosticScores(
    storedScores?.diagOwnerAnswers || [],
    storedScores?.diagBusinessAnswers || []
  );
  
  // Use diagnostic scores if available, otherwise fall back to plan response calculation
  const hasValidDiagnosticScores = diagnosticScores.ownerReadinessScore > 0 || diagnosticScores.buyerAttractivenessScore > 0;
  const ownerScore = hasValidDiagnosticScores ? diagnosticScores.ownerReadinessScore : ownerCalc.score;
  const businessScore = hasValidDiagnosticScores ? diagnosticScores.buyerAttractivenessScore : businessCalc.score;

  const categories = [
    {
      id: 'owner-readiness',
      title: 'Owner Readiness',
      description: 'Your personal and financial readiness for transition',
      score: ownerScore || ownerCalc.score,
      status: getStatus(ownerScore || ownerCalc.score),
      answeredCount: ownerCalc.answered,
      totalQuestions: ownerCalc.total,
    },
    {
      id: 'business-attractiveness',
      title: 'Business Attractiveness',
      description: 'How attractive your business is to potential buyers',
      score: businessScore || businessCalc.score,
      status: getStatus(businessScore || businessCalc.score),
      answeredCount: businessCalc.answered,
      totalQuestions: businessCalc.total,
    },
  ];

  // Use diagnostic overall score if available, else VMI score, else calculate from categories
  const calculatedOverall = Math.round(
    categories.reduce((sum, cat) => sum + cat.score, 0) / categories.length
  );
  const overallScore = hasValidDiagnosticScores 
    ? diagnosticScores.overallReadinessScore
    : (storedScores?.vmiReadinessScore 
        ? Math.round(storedScores.vmiReadinessScore) 
        : calculatedOverall);

  const overallStatus = getStatus(overallScore);

  // Identify gaps - questions with "no" answers or matching gap_action conditions
  const gaps = [];
  
  // Map of action types to recommended actions
  const actionRecommendations = {
    // Emotional & Personal Readiness
    'emotional_readiness': 'Work on developing interests and identity outside of your practice. Consider coaching or counseling to prepare emotionally for the transition.',
    'personal_readiness': 'Take time to reflect on your personal goals and what you want your life to look like after transition. Consider working with a coach to develop clarity.',
    
    // Financial Planning
    'financial_plan': 'Develop a comprehensive post-exit financial plan. Work with a financial advisor to ensure you have sufficient resources for your desired lifestyle without relying solely on business sale proceeds.',
    'financial_planning': 'Review your personal financial plan to ensure you have sufficient resources for retirement without relying on business sale proceeds.',
    'wealth_gap': 'Address your wealth gap by working with a financial planner to identify additional savings strategies or adjust your exit timeline and valuation expectations.',
    
    // Legal & Estate
    'protect_estate': 'Consult with an estate planning attorney to ensure your will, power of attorney, and shareholders\' agreement are current and properly structured for succession.',
    'legal_review': 'Schedule a legal review with an attorney experienced in succession planning. Ensure all agreements, contracts, and legal documents are in order before proceeding with the transition.',
    'estate_planning': 'Update your estate plan to reflect your succession goals. Ensure beneficiary designations, trusts, and business succession documents are aligned.',
    
    // Tax Planning
    'tax_advisor': 'Engage a qualified tax advisor immediately to develop a comprehensive tax strategy for your succession. This should be done before proceeding with any transaction to maximize your after-tax proceeds.',
    'tax_strategy': 'Develop a tax-efficient succession strategy. Work with your tax advisor to minimize tax liability through proper structuring and timing.',
    
    // Timeline & Planning
    'timeline': 'Establish a clear timeline for your succession. Having a specific target date helps create urgency and allows for proper planning of all transition activities.',
    'set_timeline': 'Set a specific target date for your succession. This creates accountability and allows for proper planning.',
    
    // Valuation & Business Value
    'valuation': 'Obtain a professional business valuation to understand your practice\'s current market value. This is essential for setting realistic expectations and negotiating effectively.',
    'get_valuation': 'Get a professional business valuation to understand your current market value and identify opportunities to increase it before transition.',
    'value_gap': 'Address the gap between your current business value and your target. Focus on the key value drivers identified in your assessment.',
    
    // Successor Development
    'successor': 'Identify and begin developing an internal successor. Internal succession typically results in better client retention and cultural continuity. Consider creating a formal development plan.',
    'identify_successor': 'Begin identifying potential internal or external successors. Consider the qualities and skills needed for a successful transition.',
    'develop_successor': 'Create a formal development plan for your identified successor. Include training, mentoring, and gradual responsibility increases.',
    
    // Operations & Documentation
    'documentation': 'Begin documenting your key processes and procedures. This documentation is critical for training your successor and ensuring business continuity during the transition.',
    'document_processes': 'Document your key business processes and client management procedures. This increases business value and enables smoother transition.',
    'operations': 'Review and optimize your business operations. Efficient, documented processes make your business more attractive to successors.',
    
    // Client Management
    'client_retention': 'Develop a client retention strategy to maximize the value of your practice during transition.',
    'client_communication': 'Plan how and when you will communicate your succession plans to clients. A well-managed communication strategy improves retention.',
    
    // Business Attractiveness
    'improve_attractiveness': 'Focus on making your business more attractive to potential buyers or successors by addressing operational inefficiencies and documenting key processes.',
    'recurring_revenue': 'Work on building more predictable, recurring revenue streams to increase business value and attractiveness.',
  };

  // Define critical questions and their recommended actions (fallback for questions without gap_action)
  const criticalTriggers = {
    'tax advisor': {
      priority: 'critical',
      keywords: ['tax advisor', 'tax consultant'],
      recommendedAction: actionRecommendations.tax_advisor,
    },
    'attorney': {
      priority: 'critical', 
      keywords: ['attorney', 'lawyer', 'legal'],
      recommendedAction: actionRecommendations.legal_review,
    },
    'timeline': {
      priority: 'high',
      keywords: ['timeline', 'target date', 'when'],
      recommendedAction: actionRecommendations.timeline,
    },
    'valuation': {
      priority: 'high',
      keywords: ['valuation', 'value', 'worth'],
      recommendedAction: actionRecommendations.valuation,
    },
    'successor': {
      priority: 'high',
      keywords: ['successor', 'candidate', 'replacement'],
      recommendedAction: actionRecommendations.successor,
    },
    'documentation': {
      priority: 'medium',
      keywords: ['document', 'process', 'procedure'],
      recommendedAction: actionRecommendations.documentation,
    },
  };

  // Helper to format an action key into readable text (fallback)
  const formatActionKey = (actionKey) => {
    if (!actionKey) return null;
    // Convert snake_case to readable text: "financial_plan" -> "Focus on financial plan"
    const readable = actionKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return `Review and address: ${readable}. Consider consulting with a professional advisor for guidance in this area.`;
  };

  // Helper to get recommendation from gap_action object or string
  const getRecommendationFromGapAction = (gapAction) => {
    if (!gapAction) return null;
    
    // If it's a string, use it directly or look up in actionRecommendations
    if (typeof gapAction === 'string') {
      return actionRecommendations[gapAction] || formatActionKey(gapAction);
    }
    
    // If it's an object with action property
    if (typeof gapAction === 'object' && gapAction.action) {
      return actionRecommendations[gapAction.action] || formatActionKey(gapAction.action);
    }
    
    return null;
  };

  // Helper to get priority from gap_action
  const getPriorityFromGapAction = (gapAction) => {
    if (!gapAction) return null;
    
    if (typeof gapAction === 'object' && gapAction.priority) {
      return gapAction.priority;
    }
    
    return null;
  };

  // Helper to check if gap_action condition matches the answer
  const doesConditionMatch = (gapAction, answer) => {
    if (!gapAction) return false;
    
    // If it's a simple gap_action without condition, trigger on "no" answers
    if (typeof gapAction === 'string') {
      return answer === 'no' || answer === 'false';
    }
    
    // If it's an object with condition
    if (typeof gapAction === 'object' && gapAction.condition) {
      const condition = String(gapAction.condition).toLowerCase();
      const answerLower = String(answer).toLowerCase();
      
      // Check if the answer contains or matches the condition
      return answerLower.includes(condition) || condition.includes(answerLower);
    }
    
    // Default: trigger on "no" answers
    return answer === 'no' || answer === 'false';
  };

  responses.forEach((r) => {
    const rawAnswer = r.answer?.value || r.answer || '';
    const answer = String(rawAnswer).toLowerCase();
    const question = questionMap[r.planQuestionId];
    
    if (!question) return;

    const gapAction = question.gapAction;
    const questionText = (question.questionText || '').toLowerCase();

    // Check if gap_action condition matches
    const gapActionMatches = doesConditionMatch(gapAction, answer);
    
    // Check if this is a "no" answer (for questions without gap_action)
    const isNoAnswer = answer === 'no' || answer === 'false';

    // Find matching critical trigger based on question text
    let matchedTrigger = null;
    for (const [key, trigger] of Object.entries(criticalTriggers)) {
      if (trigger.keywords.some((kw) => questionText.includes(kw))) {
        matchedTrigger = { key, ...trigger };
        break;
      }
    }

    // Add to gaps if gap_action matches or it's a "no" answer with a trigger
    if (gapActionMatches || (isNoAnswer && matchedTrigger)) {
      const recommendation = getRecommendationFromGapAction(gapAction) || 
                            matchedTrigger?.recommendedAction || 
                            'Review this area and take appropriate action.';
      
      const priority = getPriorityFromGapAction(gapAction) || 
                      matchedTrigger?.priority || 
                      'medium';

      gaps.push({
        priority: priority,
        questionText: question.questionText,
        userAnswer: String(rawAnswer || 'No'),
        recommendedAction: recommendation,
        moduleTitle: question.moduleTitle,
        sectionTitle: question.sectionTitle,
      });
    }
  });

  // Sort gaps by priority
  const priorityOrder = { critical: 0, high: 1, medium: 2 };
  gaps.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return {
    overallScore,
    overallStatus,
    categories,
    gaps,
  };
}

export default SuccessionReadinessAssessment;

